import { Font } from "@/types/fonts";
import { updateGlobalSettings } from "./settings";
import { breakdanceUndo } from "@/lib/undo/undo";

export type TypographyFontPair = {
  body_font: Font["slug"];
  heading_font: Font["slug"];
};

export type SingularityFontType =
  | "heading_font"
  | "body_font"
  | "buttons"
  | null;

export const TYPOGRAPHY_FONT_PAIRS: TypographyFontPair[] = [
  // Sans-serif body + Serif heading (classic and elegant)
  { body_font: "gfont-nunitosans", heading_font: "gfont-playfairdisplay" },
  { body_font: "gfont-montserrat", heading_font: "gfont-domine" },
  { body_font: "gfont-lato", heading_font: "gfont-ptserif" },
  { body_font: "gfont-nunitosans", heading_font: "gfont-librecaslontext" },
  { body_font: "gfont-opensans", heading_font: "gfont-gloock" },
  { body_font: "gfont-heebo", heading_font: "gfont-ebgaramond" },
  { body_font: "gfont-roboto", heading_font: "gfont-lora" },
  { body_font: "gfont-mulish", heading_font: "gfont-domine" },
  { body_font: "gfont-barlow", heading_font: "gfont-dmserifdisplay" },
  { body_font: "gfont-roboto", heading_font: "gfont-librecaslontext" },
  { body_font: "gfont-mulish", heading_font: "gfont-librecaslontext" },
  { body_font: "gfont-heebo", heading_font: "gfont-domine" },
  { body_font: "gfont-karla", heading_font: "gfont-gloock" },
  { body_font: "gfont-montserrat", heading_font: "gfont-playfairdisplay" },
  { body_font: "gfont-poppins", heading_font: "gfont-cinzel" },
  { body_font: "gfont-manrope", heading_font: "gfont-vollkorn" },
  { body_font: "gfont-worksans", heading_font: "gfont-crimsonpro" },

  // Sans-serif body + Sans-serif heading (modern and clean)
  { body_font: "gfont-manrope", heading_font: "gfont-nunitosans" },
  { body_font: "gfont-opensans", heading_font: "gfont-jost" },
  { body_font: "gfont-nunitosans", heading_font: "gfont-raleway" },
  { body_font: "gfont-raleway", heading_font: "gfont-montserrat" },
  { body_font: "gfont-inter", heading_font: "gfont-karla" },
  { body_font: "gfont-firasans", heading_font: "gfont-nunitosans" },
  { body_font: "gfont-jost", heading_font: "gfont-opensans" },
  { body_font: "gfont-firasans", heading_font: "gfont-ibmplexsans" },
  { body_font: "gfont-dmsans", heading_font: "gfont-raleway" },
  { body_font: "gfont-quicksand", heading_font: "gfont-mulish" },
  { body_font: "gfont-opensans", heading_font: "gfont-inter" },
  { body_font: "gfont-lato", heading_font: "gfont-ibmplexsans" },
  { body_font: "gfont-opensans", heading_font: "gfont-lato" },
  { body_font: "gfont-nunitosans", heading_font: "gfont-jost" },
  { body_font: "gfont-montserrat", heading_font: "gfont-karla" },
  { body_font: "gfont-raleway", heading_font: "gfont-poppins" },
  { body_font: "gfont-raleway", heading_font: "gfont-dmsans" },

  // Serif body + Sans-serif heading (expressive and fresh)
  { body_font: "gfont-crimsonpro", heading_font: "gfont-montserrat" },
  { body_font: "gfont-lora", heading_font: "gfont-dmsans" },
  { body_font: "gfont-cardo", heading_font: "gfont-firasans" },
  { body_font: "gfont-domine", heading_font: "gfont-poppins" },
  { body_font: "gfont-rosarivo", heading_font: "gfont-jost" },
  { body_font: "gfont-vollkorn", heading_font: "gfont-firasans" },
  { body_font: "gfont-domine", heading_font: "gfont-manrope" },
  { body_font: "gfont-ebgaramond", heading_font: "gfont-lato" },
  { body_font: "gfont-ebgaramond", heading_font: "gfont-manrope" },
  { body_font: "gfont-alegreya", heading_font: "gfont-montserrat" },
  { body_font: "gfont-lora", heading_font: "gfont-mulish" },
  { body_font: "gfont-gloock", heading_font: "gfont-manrope" },
  { body_font: "gfont-crimsonpro", heading_font: "gfont-ubuntu" },
  { body_font: "gfont-alegreya", heading_font: "gfont-karla" },
  { body_font: "gfont-ptserif", heading_font: "gfont-montserrat" },
  { body_font: "gfont-ptserif", heading_font: "gfont-lato" },

  // Fun & funky pairings (expressive, creative, bold)
  { body_font: "gfont-fredoka", heading_font: "gfont-lobster" },
  { body_font: "gfont-comfortaa", heading_font: "gfont-pacifico" },
  { body_font: "gfont-rubik", heading_font: "gfont-chewy" },
  { body_font: "gfont-kumbhsans", heading_font: "gfont-lemon" },
  { body_font: "gfont-quicksand", heading_font: "gfont-righteous" },
  { body_font: "gfont-signika", heading_font: "gfont-monoton" },
  { body_font: "gfont-mavenpro", heading_font: "gfont-alfaslabone" },
  { body_font: "gfont-urbanist", heading_font: "gfont-bungee" },
  { body_font: "gfont-balsamiqsans", heading_font: "gfont-permanentmarker" },
  { body_font: "gfont-sen", heading_font: "gfont-russoone" }
];

export function randomTypographyFontPair(): TypographyFontPair {
  return TYPOGRAPHY_FONT_PAIRS[
    Math.floor(Math.random() * TYPOGRAPHY_FONT_PAIRS.length)
  ];
}

export function shuffleTypography(): void {
  const { body_font, heading_font } = randomTypographyFontPair();
  breakdanceUndo.transaction(() => {
    updateGlobalTypography("body_font", body_font);
    updateGlobalTypography("heading_font", heading_font);
    updateGlobalTypography("buttons", body_font);
  }, "Shuffled typography");
}

export function updateGlobalTypography(
  type: SingularityFontType,
  slug: Font["slug"]
): void {
  if (!type) return;

  if (type === "buttons") {
    updateGlobalButtonsTypography(slug);
    return;
  }
  updateGlobalSettings("settings.typography", { [type]: slug });
}

export function updateGlobalButtonsTypography(slug: Font["slug"]): void {
  updateGlobalSettings(
    [
      "settings.buttons.primary.typography.typography.custom.customTypography.fontFamily",
      "settings.buttons.secondary.typography.typography.custom.customTypography.fontFamily"
    ],
    {
      breakpoint_base: slug
    }
  );
}

export function updateGlobalTypographyRatio(ratio: number): void {
  updateGlobalSettings("settings.typography", {
    ratio: { breakpoint_base: ratio }
  });
}
